import React, { useRef, useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getSellerInfo } from '../../api';

/* =========================================================
   GLOBAL STYLE CONSTANTS
========================================================= */
const BORDER = '1.5px solid #222';
const INVOICE_HEADING_COLOR = '#cce3fa';
const ITEM_HEADER_COLOR = INVOICE_HEADING_COLOR;
const ITEM_HEADER_BOTTOM_BORDER = '2px solid #334155';

const cellBorder = {
  borderLeft: BORDER,
  borderRight: BORDER,
  borderBottom: BORDER,
  borderTop: BORDER,
  p: 0.5,
  fontSize: 9,
};

const headerCellBorder = {
  ...cellBorder,
  borderBottom: ITEM_HEADER_BOTTOM_BORDER,
  fontSize: '8px',
  lineHeight: 1.1,
  fontWeight: 700,
};

// Blank filler rows: vertical separators only (no horizontal lines)
const blankCellBorder = {
  borderLeft: BORDER,
  borderRight: BORDER,
  borderTop: 'none',
  borderBottom: 'none',
  p: 0.5,
  fontSize: 10,
};

/* =========================================================
   COMPONENT
========================================================= */
export default function InvoiceTemplate({ invoice, items = [], copyType = null, autoPrint = false, onReady = null, hideActions = false }) {
  const printRef = useRef();
  const [sellerInfo, setSellerInfo] = useState(null);
  const [loadingSeller, setLoadingSeller] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await getSellerInfo();
        setSellerInfo(res?.data || null);
      } catch (err) {
        setSellerInfo(null);
      } finally {
        setLoadingSeller(false);
      }
    })();
  }, []);
  useEffect(() => {
    if (!autoPrint || loadingSeller || !invoice) return;
    if (!printRef.current) return;
    const t = setTimeout(() => {
      if (printRef.current) {
        handlePrint();
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, loadingSeller, invoice, items]);

  useEffect(() => {
    if (!loadingSeller && invoice && onReady) {
      onReady();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSeller, invoice]);
  if (loadingSeller) return <div>Loading seller details...</div>;
      // Print handler: open invoice in new window for printing
  const handlePrint = () => {
    if (!printRef.current) return;
    const invoiceNoVal = invoice?.invoice_no || 'Invoice';
    const receiverName = invoice?.party_name || invoice?.customer_name || invoice?.vendor_name || '';
    const title = receiverName ? `${invoiceNoVal} - ${receiverName}`.replace(/[\\/:*?"<>|]/g, '_') : invoiceNoVal;

    // Get all computed styles to carry over fonts/layout
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${title}</title>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" />
  <style>
    @page { size: A4 portrait; margin: 3mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { width: 100%; height: 100%; font-family: 'Roboto', Arial, sans-serif; }
    @media print {
      @page { size: A4 portrait; margin: 3mm; }
      html, body { width: 100%; height: 100%; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .print-invoice-only { width: 204mm !important; padding: 0 !important; }
    }
  </style>
</head>
<body></body>
</html>`);

    // Clone all stylesheets into the print window
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    links.forEach(link => {
      const newLink = printWindow.document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = link.href;
      printWindow.document.head.appendChild(newLink);
    });

    // Copy all CSS rules from every stylesheet. Reading cssRules (instead of
    // <style>.textContent) is required because MUI/Emotion injects rules via
    // the CSSOM in production builds, leaving the <style> textContent empty.
    Array.from(document.styleSheets).forEach(sheet => {
      let rules;
      try {
        rules = sheet.cssRules || sheet.rules;
      } catch (err) {
        // Cross-origin stylesheet (e.g. Google Fonts) cannot be read; the
        // matching <link> was already copied above.
        return;
      }
      if (!rules || !rules.length) return;
      const cssText = Array.from(rules).map(rule => rule.cssText).join('\n');
      const newStyle = printWindow.document.createElement('style');
      newStyle.textContent = cssText;
      printWindow.document.head.appendChild(newStyle);
    });

    // Clone the invoice element
    const invoiceClone = printRef.current.cloneNode(true);
    invoiceClone.classList.add('print-invoice-only');
    invoiceClone.style.width = '100%';
    invoiceClone.style.maxWidth = '100%';
    invoiceClone.style.margin = '0';
    invoiceClone.style.padding = '0';
    invoiceClone.style.boxSizing = 'border-box';
    invoiceClone.style.background = '#fff';
    invoiceClone.style.border = 'none';
    invoiceClone.style.boxShadow = 'none';

    // Remove Go Back / Print buttons from clone
    invoiceClone.querySelectorAll('button, .MuiButton-root, .print-hint').forEach(el => el.remove());

    printWindow.document.body.appendChild(invoiceClone);

    // Wait for styles to load then trigger print
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 800);
  };
    // Helper to trim .00 from numbers/strings
    function trimZero(val) {
      if (typeof val === 'number') val = val.toFixed(2);
      if (typeof val !== 'string') return val;
      return val.replace(/\.00$/, '').replace(/(\.[1-9]*)0+$/, '$1');
    }
  // ...existing code...

  /* -------------------- SELLER & PARTY DATA -------------------- */
  // SELLER INFO (from props, fallback to demo values)
  const seller = sellerInfo || {};
  const business = {
    name: seller.store_name || seller.name || seller.company || 'STORE NAME',
    address: seller.formatted_address ||
      `${seller.street || 'Street address'}, ${seller.city_name || 'City'}, ${seller.state || 'State'}, ${seller.pincode_value || seller.pincode || '000000'}`,
    email: seller.email || 'Email',
    mobile: seller.mobile || seller.phone || seller.contact_no || 'Mobile',
    gstin: seller.tax_number || 'GST Number',
  };
  const watermarkName = business.name.toUpperCase();

  // PARTY INFO
  const party = invoice?.party_name || invoice?.customer_name || 'Gupta Hospitality Services Pvt.Ltd.';
  const partyAddress =
    invoice?.party_address ||
    invoice?.partyAddr ||
    invoice?.billing_address ||
    'Sco No.2, Sector-10a, Gurugram, Gurgaon, Haryana, 122001';
  const partyState = invoice?.party_state || invoice?.state || 'Haryana';
  const partyGstin = invoice?.party_gstin || invoice?.gstin || '-';
  // const partyStateCode = invoice?.state_code || '06';

  // CONSIGNEE INFO (from invoice.consignee joined by backend, or extra.consignee from form)
  const consignee = invoice?.consignee || invoice?.extra?.consignee || null;
  const consigneeName = consignee?.name || party;
  const consigneeAddress =
    consignee && consignee.address
      ? `${consignee.address}${consignee.city ? ', ' + consignee.city : ''}${consignee.state ? ', ' + consignee.state : ''}${consignee.pincode ? ' - ' + consignee.pincode : ''}`
      : partyAddress;
  const consigneeState = consignee?.state || partyState;
  const consigneeGstin = consignee?.gstin || partyGstin;

  const invoiceNo = invoice?.invoice_no || 'INV0001';
  // Format date as DD-MM-YYYY if ISO string, using UTC to avoid timezone shift
  let invoiceDate = invoice?.sale_date || invoice?.purchase_date || invoice?.proforma_date || '01-01-2026';
  if (invoiceDate && typeof invoiceDate === 'string' && invoiceDate.includes('T')) {
    const d = new Date(invoiceDate);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    invoiceDate = `${day}-${month}-${year}`;
  }
  const reverseCharge = invoice?.reverse_charge || 'NO';

  // SELLER BANK DETAILS (from sellerInfo, fallback to demo values)
  const bank = {
    bankName: seller.bank_name || 'BANK NAME',
    accName: seller.account_name || '-',
    accNo: seller.account_number || '-',
    ifsc: seller.bank_ifsc_code || '-',
  };

  /* -------------------- CALCULATIONS -------------------- */
  // Calculate CGST and SGST from gst_percent if present
  const getTaxable = (i) => Number(i.taxable_value ?? i.price ?? 0);
  const getGST = (i) => Number(i.gst_percent ?? i.gst ?? 0);
  const getCGST = (i) => getTaxable(i) * getGST(i) / 200;
  const getSGST = (i) => getTaxable(i) * getGST(i) / 200;

  const totalQty = items.reduce((sum, i) => sum + Number(i.qty || 0), 0);
  const totalTaxable = items.reduce((sum, i) => sum + getTaxable(i), 0);
  const cgst = items.reduce((sum, i) => sum + getCGST(i), 0);
  const sgst = items.reduce((sum, i) => sum + getSGST(i), 0);
  const totalTax = cgst + sgst;
  const totalAmount = totalTaxable + totalTax;

  // Determine if any tax is applied on items
  const hasTaxApplied = items.some((i) => getGST(i) > 0);
  // Invoice heading: purchase vs sale, and tax invoice vs bill of supply
  const isPurchase = !!(invoice?.purchase_date);
  const invoiceType = invoice?.invoice_type || 'tax-invoice';
  let invoiceTitle;
  if (isPurchase) {
    invoiceTitle = 'PURCHASE INVOICE';
  } else if (invoiceType === 'bill-of-supply') {
    invoiceTitle = 'BILL OF SUPPLY';
  } else if (invoiceType === 'proforma-invoice') {
    invoiceTitle = 'PROFORMA INVOICE';
  } else if (invoiceType === 'general-invoice') {
    invoiceTitle = 'GENERAL INVOICE';
  } else {
    invoiceTitle = hasTaxApplied ? 'TAX INVOICE' : 'GENERAL INVOICE';
  }

  // Terms & conditions: use the saved value from the invoice, else the safe defaults
  const termsText = invoice?.terms_conditions || invoice?.extra?.terms_conditions || (
    '1. This is an electronically generated document.\n2. All disputes are subject to Gurugram jurisdiction.\n3. If the cheque is dishonoured, a penalty of ₹500 + interest @2% per month on the outstanding amount will be charged.\n4. Payment shall be made within the credit period agreed; a late payment interest of 2% per month shall apply on overdue amounts.\n5. Goods once sold will not be taken back or exchanged, except for manufacturing defect or damaged/wrong delivery reported within 48 hours of delivery.\n6. The seller reserves the right to withhold delivery of further goods if any previous payment is overdue.\n7. Any shortfall/extra delivery, damages or discrepancies must be reported within 48 hours of delivery.\n8. Title of goods passes to the buyer only after full payment is received by the seller.\n9. Prices are subject to change without prior notice unless confirmed by a written quotation/order.\n10. This invoice is valid only if duly signed and stamped by the authorised signatory.'
  );
  const termsLines = termsText.split('\n').map(l => l.trim()).filter(Boolean);

  // Extract copy type — stored directly on the invoice object (DB columns), or passed as prop
  const selectedCopyTypes = copyType || {
    original_for_recipient: invoice?.original_for_recipient ?? invoice?.extra?.original_for_recipient ?? true,
    duplicate_for_transporter: invoice?.duplicate_for_transporter ?? invoice?.extra?.duplicate_for_transporter ?? false,
    triplicate_for_supplier: invoice?.triplicate_for_supplier ?? invoice?.extra?.triplicate_for_supplier ?? false,
  };
  // Build list of active copy labels
  const activeCopies = [];
  if (selectedCopyTypes.original_for_recipient) activeCopies.push('Original For Recipient');
  if (selectedCopyTypes.duplicate_for_transporter) activeCopies.push('Duplicate For Transporter');
  if (selectedCopyTypes.triplicate_for_supplier) activeCopies.push('Triplicate For Supplier');
  // If no copy selected, default to Original
  const displayCopies = activeCopies.length > 0 ? activeCopies : ['Original For Recipient'];

  /* -------------------- PDF HANDLER (jsPDF+autoTable for selectable text) -------------------- */
  const generateInvoicePDFPage = (doc, copyLabel, isFirstPage) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginPt = pageWidth * 0.01; // 1% margin
    let y = marginPt + 16;

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(business.name, pageWidth / 2, y, { align: 'center' });
    y += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(business.address, pageWidth / 2, y, { align: 'center' });
    y += 16;
    doc.text(`Email: ${business.email}   Mobile: ${business.mobile}`, pageWidth / 2, y, { align: 'center' });
    y += 16;
    doc.text(`GSTIN : ${business.gstin}`, pageWidth / 2, y, { align: 'center' });
    y += 18;

    // Invoice Title
    doc.setFillColor(204, 227, 250);
    doc.rect(40, y - 14, pageWidth - 80, 28, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(invoiceTitle, pageWidth / 2, y, { align: 'center' });
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(11);
    doc.text(copyLabel, pageWidth - 60, y, { align: 'right' });
    y += 24;

    // Invoice Info
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    const infoLeft = [
      'Invoice Number',
      'Invoice Date',
      'State',
      'Reverse Charge',
    ];
    const infoRight = [
      invoiceNo,
      invoiceDate,
      partyState,
      reverseCharge,
    ];
    let infoY = y;
    for (let i = 0; i < infoLeft.length; i++) {
      doc.text(infoLeft[i], 50, infoY);
      doc.setFont('helvetica', i === 0 ? 'bold' : 'normal');
      doc.text(String(infoRight[i]), pageWidth - 50, infoY, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      infoY += 16;
    }
    y = infoY + 4;

    // Party Details
    doc.setFont('helvetica', 'bold');
    doc.text('Details of Receiver | Billed to', 50, y);
    doc.text('Details of Consignee | Shipped to', pageWidth / 2 + 20, y);
    doc.setFont('helvetica', 'normal');
    y += 14;
    const partyDetails = [
      [`Name: ${party}`, `Name: ${consigneeName}`],
      [`Address: ${partyAddress}`, `Address: ${consigneeAddress}`],
      [`GSTIN: ${partyGstin}`, `GSTIN: ${consigneeGstin}`],
      [`State: ${partyState}`, `State: ${consigneeState}`],
    ];
    for (let i = 0; i < partyDetails.length; i++) {
      doc.text(partyDetails[i][0], 50, y);
      doc.text(partyDetails[i][1], pageWidth / 2 + 20, y);
      y += 14;
    }
    y += 6;

    // Items Table
    const tableColumns = [
      { header: 'Sr', dataKey: 'sr' },
      { header: 'Name of Item/Product', dataKey: 'name' },
      { header: 'MRP', dataKey: 'mrp' },
      { header: 'HSN', dataKey: 'hsn' },
      { header: 'Qty', dataKey: 'qty' },
      { header: 'Unit', dataKey: 'unit' },
      { header: 'Rate', dataKey: 'rate' },
      { header: 'Taxable Value', dataKey: 'taxable' },
      { header: 'CGST Rate', dataKey: 'cgst_rate' },
      { header: 'CGST Amt', dataKey: 'cgst_amt' },
      { header: 'SGST Rate', dataKey: 'sgst_rate' },
      { header: 'SGST Amt', dataKey: 'sgst_amt' },
      { header: 'Total', dataKey: 'total' },
    ];
    const tableRows = items.map((item, idx) => {
      const taxable = getTaxable(item);
      const gst = getGST(item);
      const cgstVal = getCGST(item);
      const sgstVal = getSGST(item);
      return {
        sr: trimZero(idx + 1),
        name: item.product_name || item.name || item.item_name || item.product || item.description || '—',
        subtext: item.subtext || '',
        mrp: trimZero(item.mrp),
        hsn: trimZero(item.hsn),
        qty: trimZero(item.qty),
        unit: trimZero(item.unit),
        rate: trimZero(item.price),
        taxable: trimZero(taxable),
        cgst_rate: gst ? trimZero((gst/2).toFixed(2)) + '%' : '-',
        cgst_amt: cgstVal ? trimZero(cgstVal) : trimZero(item.cgst_amt || '0.00'),
        sgst_rate: gst ? trimZero((gst/2).toFixed(2)) + '%' : '-',
        sgst_amt: sgstVal ? trimZero(sgstVal) : trimZero(item.sgst_amt || '0.00'),
        total: trimZero(taxable + cgstVal + sgstVal),
      };
    });
    // Add totals row
    tableRows.push({
      sr: '',
      name: 'Total',
      subtext: '',
      mrp: '',
      hsn: '',
      qty: trimZero(totalQty),
      unit: '',
      rate: '',
      taxable: trimZero(totalTaxable),
      cgst_rate: '',
      cgst_amt: trimZero(cgst),
      sgst_rate: '',
      sgst_amt: trimZero(sgst),
      total: trimZero(totalAmount),
    });
    // Add blank rows to fill A4 page height (before total row)
    const blankRowCount = Math.max(0, 12 - items.length);
    const totalRow = tableRows.pop();
    for (let i = 0; i < blankRowCount; i++) {
      tableRows.push({ sr: '', name: '', subtext: '', mrp: '', hsn: '', qty: '', unit: '', rate: '', taxable: '', cgst_rate: '', cgst_amt: '', sgst_rate: '', sgst_amt: '', total: '' });
    }
    tableRows.push(totalRow);
    doc.autoTable({
      startY: y,
      head: [tableColumns.map(col => col.header)],
      body: tableRows.map(row => tableColumns.map(col => row[col.dataKey])),
      styles: { fontSize: 10, cellPadding: 2, halign: 'center', valign: 'middle', lineWidth: 0.1, lineColor: [34, 34, 34] },
      headStyles: { fillColor: [204, 227, 250], textColor: 20, fontStyle: 'bold', lineWidth: 0.1, lineColor: [34, 34, 34] },
      columnStyles: {
        1: { halign: 'left' },
      },
      theme: 'grid',
      margin: { left: 40, right: 40 },
      tableWidth: pageWidth - 80,
      didParseCell: function (data) {
        // Detect blank filler rows (no text in any cell) and remove horizontal lines
        const rowData = tableRows[data.row?.index];
        const hasSubtext = !!(rowData && rowData.subtext);
        const rowCells = data.row?.cells ? Object.values(data.row.cells) : [];
        const isBlank = !hasSubtext && rowCells.length > 0 && rowCells.every(c => !String(c.text || '').trim());
        if (data.section === 'body' && isBlank) {
          data.cell.styles.lineWidth = { top: 0, bottom: 0, left: 0.1, right: 0.1 };
          data.cell.styles.lineColor = [34, 34, 34];
        } else {
          // Keep full grid borders (horizontal + vertical) for a proper table
          data.cell.styles.lineWidth = 0.1;
          data.cell.styles.lineColor = [34, 34, 34];
        }
        // Reserve space in the item name cell and render the subtext tagline in didDrawCell
        if (data.section === 'body' && data.column.index === 1 && hasSubtext) {
          data.cell.text = [];
          data.cell.styles.valign = 'top';
          data.cell.styles.cellPadding = { top: 3, right: 2, bottom: 3, left: 2 };
          data.cell.styles.minCellHeight = 34;
        }
      },
      didDrawCell: function (data) {
        if (data.section === 'body' && data.column.index === 1) {
          const rowData = tableRows[data.row?.index];
          if (rowData && rowData.subtext) {
            const d = data.doc;
            const cell = data.cell;
            const x = cell.x + 2;
            d.setFont('helvetica', 'normal');
            d.setFontSize(10);
            d.text(String(rowData.name), x, cell.y + 14, { align: 'left' });
            d.setFont('helvetica', 'italic');
            d.setFontSize(8);
            d.text(String(rowData.subtext), x, cell.y + 24, { align: 'left' });
          }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 12;

    // Summary Box (right side)
    let summaryY = y;
    let summaryX = pageWidth - 220;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Before Tax', summaryX, summaryY);
    doc.text(`₹ ${trimZero(totalTaxable)}`, summaryX + 120, summaryY, { align: 'right' });
    summaryY += 16;
    doc.text('Added CGST', summaryX, summaryY);
    doc.text(`₹ ${trimZero(cgst)}`, summaryX + 120, summaryY, { align: 'right' });
    summaryY += 14;
    doc.text('Added SGST', summaryX, summaryY);
    doc.text(`₹ ${trimZero(sgst)}`, summaryX + 120, summaryY, { align: 'right' });
    summaryY += 16;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Tax', summaryX, summaryY);
    doc.text(`₹ ${trimZero(totalTax)}`, summaryX + 120, summaryY, { align: 'right' });
    summaryY += 16;
    doc.setFillColor(204, 227, 250);
    doc.rect(summaryX - 4, summaryY - 12, 160, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Final Amount', summaryX, summaryY);
    doc.text(`₹ ${trimZero(totalAmount)}`, summaryX + 120, summaryY, { align: 'right' });
    summaryY += 18;
    doc.setFont('helvetica', 'bold');
    doc.text('Balance Due', summaryX, summaryY);
    doc.text(`₹ ${trimZero(totalAmount)}`, summaryX + 120, summaryY, { align: 'right' });

    // Amount in words (left side)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total Invoice Amount in Words', 50, y);
    doc.setFont('helvetica', 'italic');
    doc.text(`${numberToWords(totalAmount)} Only /-`, 50, y + 16);

    // Bank details (left side)
    let bankY = y + 40;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Bank and Payment Details', 50, bankY);
    doc.setFont('helvetica', 'normal');
    bankY += 14;
    doc.text(`Account Name : ${bank.name}`, 50, bankY);
    bankY += 14;
    doc.text(`Account No : ${bank.accNo}`, 50, bankY);
    bankY += 14;
    doc.text(`IFSC : ${bank.ifsc}`, 50, bankY);
    bankY += 14;
    doc.text(`Bank : ${bank.bankName}`, 50, bankY);
    bankY += 14;
    doc.text(`Branch : ${bank.branch}`, 50, bankY);
    bankY += 14;
    doc.text(`Swift : ${bank.swift}`, 50, bankY);

    // Terms and signature
    let termsY = Math.max(summaryY + 12, bankY + 12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Terms And Conditions', 50, termsY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    termsLines.forEach((line, i) => {
      doc.text(`• ${line}`, 50, termsY + 12 + (i * 10));
    });
    const termsBottom = termsY + 12 + (termsLines.length * 10);

    // Signature
    const sigY = Math.max(termsY + 40, termsBottom - 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Certified that the particulars given above are true and correct', pageWidth - 220, sigY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`For, ${business.name}`, pageWidth - 220, sigY + 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Authorised Signatory', pageWidth - 220, sigY + 28);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    // Generate a page for each selected copy
    displayCopies.forEach((copyLabel, idx) => {
      if (idx > 0) doc.addPage();
      generateInvoicePDFPage(doc, copyLabel, idx === 0);
    });
    const billedTo = invoice?.party_name || invoice?.customer_name || invoice?.vendor_name || '';
    const baseName = billedTo ? `${invoiceNo} - ${billedTo}` : invoiceNo;
    doc.save(`${baseName.replace(/[\\/:*?"<>|]/g, '_')}.pdf`);
  };

  /* =========================================================
     RENDER - Reusable invoice body
  ========================================================= */
  const renderInvoiceBody = (copyLabel, boxKey) => (
    <Box key={boxKey}>
      {/* ================= HEADER ================= */}
        <Box sx={{ borderTop: BORDER, borderLeft: BORDER, borderRight: BORDER, borderBottom: BORDER }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, textAlign: 'center', mt: 0.5, fontSize: '1.2rem !important', lineHeight: 1.15, '@media print': { fontSize: '24px !important' } }}
          >
            {business.name.toUpperCase()}
          </Typography>
          <Typography sx={{ textAlign: 'center', fontSize: 14, mt: 0.15, lineHeight: 1.2 }}>
            {business.address}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pb: 0.45,
              mt: 0.15,
            }}
          >
            <Typography fontSize={13} sx={{ lineHeight: 1.2 }}>
              Email: {business.email}   Mobile: {business.mobile}
            </Typography>
            <Typography fontSize={13} sx={{ lineHeight: 1.2 }}>
              GSTIN : {business.gstin}
            </Typography>
          </Box>
        </Box>

        {/* ================= TITLE ================= */}
        <Box
          className="print-bg"
          sx={{
            bgcolor: INVOICE_HEADING_COLOR,
            borderBottom: BORDER,
            borderLeft: BORDER,
            borderRight: BORDER,
            borderTop: BORDER,
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Typography variant="h5" fontWeight={700} py={1}>
            {invoiceTitle}
          </Typography>
          <Typography
            data-copy-label
            sx={{
              position: 'absolute',
              right: 16,
              top: 10,
              fontStyle: 'italic',
              fontSize: 10,
            }}
          >
            {copyLabel}
          </Typography>
        </Box>

        {/* ================= INVOICE INFO ================= */}
        <Box sx={{ borderBottom: BORDER, borderLeft: BORDER, borderRight: BORDER, p: 1, textAlign: 'left' }}>
          <div style={{ fontSize: 10 }}>Invoice Number: <b>{invoiceNo}</b></div>
          <div style={{ fontSize: 10 }}>Invoice Date: {invoiceDate}</div>
          <div style={{ fontSize: 10 }}>State: {partyState}</div>
          <div style={{ fontSize: 10 }}>Reverse Charge: {reverseCharge}</div>
        </Box>

        {/* ================= PARTY DETAILS ================= */}
        <Box sx={{ display: 'flex', borderBottom: BORDER, borderLeft: BORDER, borderRight: BORDER }}>
          <Box sx={{ flex: 1, p: 1, borderRight: BORDER }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Details of Receiver | Billed to</span>
            <div style={{ fontSize: 10 }}>Name: {party}</div>
            <div style={{ fontSize: 10 }}>Address: {partyAddress}</div>
            <div style={{ fontSize: 10 }}>GSTIN: {partyGstin}</div>
            <div style={{ fontSize: 10 }}>State: {partyState}</div>
          </Box>

          <Box sx={{ flex: 1, p: 1 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Details of Consignee | Shipped to</span>
            <div style={{ fontSize: 10 }}>Name: {consigneeName}</div>
            <div style={{ fontSize: 10 }}>Address: {consigneeAddress}</div>
            <div style={{ fontSize: 10 }}>GSTIN: {consigneeGstin}</div>
            <div style={{ fontSize: 10 }}>State: {consigneeState}</div>
          </Box>
        </Box>

        {/* ================= ITEMS TABLE ================= */}
        <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, borderLeft: BORDER, borderRight: BORDER, borderBottom: BORDER, overflowX: 'auto', maxWidth: '100%' }}>
          <Table size="small" sx={{
            borderCollapse: 'collapse',
            tableLayout: 'auto',
            width: '100%',
            minWidth: 720,
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontSize: '8px !important',
              lineHeight: '1.1 !important',
              fontWeight: '700 !important',
              backgroundColor: `${ITEM_HEADER_COLOR} !important`,
              borderBottom: `${ITEM_HEADER_BOTTOM_BORDER} !important`,
              whiteSpace: 'nowrap !important',
              wordBreak: 'normal !important',
              overflowWrap: 'normal !important',
            },
            '& .MuiTableCell-root:not(.product-column)': {
              whiteSpace: 'nowrap',
            },
            '& .product-column': {
              width: '100% !important',
              whiteSpace: 'normal !important',
            },
          }}>
            <TableHead>
              <TableRow className="print-bg" sx={{ bgcolor: ITEM_HEADER_COLOR }}>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Sr</TableCell>
                <TableCell className="product-column" rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, width: 'auto', wordBreak: 'break-word' }}>Name of Item/Product</TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>MRP</TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>HSN</TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Qty</TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Unit</TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Rate</TableCell>
                <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Taxable<br />Value</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ ...headerCellBorder, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>CGST</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ ...headerCellBorder, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>SGST</TableCell>
                  <TableCell rowSpan={2} sx={{ ...headerCellBorder, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>Total</TableCell>
              </TableRow>
              <TableRow className="print-bg" sx={{ bgcolor: ITEM_HEADER_COLOR }}>
                <TableCell sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Rate</TableCell>
                <TableCell sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Amt</TableCell>
                <TableCell sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Rate</TableCell>
                <TableCell sx={{ ...headerCellBorder, fontWeight: 700, wordBreak: 'break-word' }}>Amt</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((item, idx) => {
                const taxable = getTaxable(item);
                const gst = getGST(item);
                const cgstVal = getCGST(item);
                const sgstVal = getSGST(item);
                return (
                  <TableRow key={idx}>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(idx + 1)}</TableCell>
                    <TableCell className="product-column" align="left" sx={{ ...cellBorder, wordBreak: 'break-word' }}>
                      {item.product_name || item.name || item.item_name || item.product || item.description || '—'}
                      {item.subtext ? (
                        <Box component="div" sx={{ fontSize: '0.5rem', fontStyle: 'italic', color: '#555', mt: 0.1 }}>
                          {item.subtext}
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(item.mrp)}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(item.hsn)}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(item.qty)}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(item.unit)}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(item.price)}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{trimZero(taxable)}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{gst ? trimZero((gst/2).toFixed(2)) + '%' : '-'}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{cgstVal ? trimZero(cgstVal) : trimZero(item.cgst_amt || '0.00')}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{gst ? trimZero((gst/2).toFixed(2)) + '%' : '-'}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, wordBreak: 'break-word' }}>{sgstVal ? trimZero(sgstVal) : trimZero(item.sgst_amt || '0.00')}</TableCell>
                    <TableCell align="center" sx={{ ...cellBorder, fontWeight: 700, wordBreak: 'break-word' }}>{trimZero(taxable + cgstVal + sgstVal)}</TableCell>
                  </TableRow>
                );
              })}

              {/* Blank rows to fill A4 page height */}
              {Array.from({ length: Math.max(0, 12 - items.length) }).map((_, i) => (
                <TableRow key={`blank-${i}`}>
                  <TableCell sx={blankCellBorder}>&nbsp;</TableCell>
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                  <TableCell sx={blankCellBorder} />
                </TableRow>
              ))}

              {/* Totals */}
              <TableRow sx={{ bgcolor: INVOICE_HEADING_COLOR, borderTop: BORDER }}>
                <TableCell colSpan={4} align="right" sx={{ ...cellBorder, fontWeight: 700 }}>Total</TableCell>
                <TableCell align="center" sx={cellBorder}>{trimZero(totalQty)}</TableCell>
                <TableCell align="center" sx={cellBorder} />
                <TableCell align="center" sx={cellBorder} />
                <TableCell align="center" sx={cellBorder}>{trimZero(totalTaxable)}</TableCell>
                <TableCell align="center" sx={cellBorder} />
                <TableCell align="center" sx={cellBorder}>{trimZero(cgst)}</TableCell>
                <TableCell align="center" sx={cellBorder} />
                <TableCell align="center" sx={cellBorder}>{trimZero(sgst)}</TableCell>
                <TableCell align="center" sx={{ ...cellBorder, fontWeight: 700 }}>{trimZero(totalAmount)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ================= SUMMARY ================= */}
        <Box sx={{ display: 'flex', borderTop: BORDER, borderLeft: BORDER, borderRight: BORDER, borderBottom: BORDER }}>
          {/* LEFT */}
          <Box sx={{ flex: 2, borderRight: BORDER }}>
            <Box sx={{ borderBottom: BORDER, p: 1, textAlign: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Total Invoice Amount in Words</span>
              <div style={{ fontStyle: 'italic', fontWeight: 700, fontSize: 10 }}>
                {numberToWords(totalAmount)} Only /-
              </div>
            </Box>

            <Box sx={{ p: 1 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Bank and Payment Details</span>
              <div style={{ fontSize: 10 }}><b>Bank Name:</b> {bank.bankName}</div>
              <div style={{ fontSize: 10 }}><b>A/C Holder Name:</b> {bank.accName}</div>
              <div style={{ fontSize: 10 }}><b>A/C Number:</b> {bank.accNo}</div>
              <div style={{ fontSize: 10 }}><b>IFSC Code:</b> {bank.ifsc}</div>
            </Box>
          </Box>

          {/* RIGHT */}
          <Box sx={{ flex: 1 }}>
            {/* Total Before Tax */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3, px: 1, minHeight: 22, fontSize: 11, borderBottom: BORDER }}>
              <span>Total Before Tax</span>
              <span>₹ {trimZero(totalTaxable)}</span>
            </Box>
            {/* Add CGST and Add SGST as list in one box */}
            <Box sx={{ py: 0.1, px: 1, fontSize: 11, borderBottom: BORDER }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 18 }}>
                <span>Added CGST</span>
                <span>₹ {trimZero(cgst)}</span>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 18 }}>
                <span>Added SGST</span>
                <span>₹ {trimZero(sgst)}</span>
              </Box>
            </Box>
            {/* Total Tax */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3, px: 1, minHeight: 22, fontSize: 11, borderBottom: BORDER, fontWeight: 700 }}>
              <span>Total Tax</span>
              <span>₹ {trimZero(totalTax)}</span>
            </Box>
            {/* Final Amount (highlighted) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3, px: 1, minHeight: 22, fontSize: 11, borderBottom: BORDER, fontWeight: 700, bgcolor: '#cce3fa' }}>
              <span>Final Amount</span>
              <span>₹ {trimZero(totalAmount)}</span>
            </Box>
            {/* Balance Due */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3, px: 1, minHeight: 22, fontSize: 11, fontWeight: 700, borderBottom: BORDER }}>
              <span>Balance Due</span>
              <span>₹ {trimZero(totalAmount)}</span>
            </Box>
          </Box>
        </Box>

        {/* ================= TERMS ================= */}
        <Box sx={{ display: 'flex', borderTop: BORDER, borderBottom: BORDER, borderRight: BORDER, borderLeft: BORDER }}>
          <Box sx={{ flex: 2, p: 1, borderRight: BORDER }}>
            <span style={{ fontWeight: 600, fontSize: 11 }}>Terms And Conditions</span>
            <ul style={{ margin: 0, paddingLeft: 14, fontSize: 9 }}>
              {termsLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 1,
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <div style={{ fontSize: 9 }}>
              Certified that the particulars given above are true and correct
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, mt: 1 }}>For, {seller.store_name || 'SELLER NAME'}</div>
            {invoice?.sign_stamp_image && (
              <img src={invoice.sign_stamp_image} alt="Authorised stamp" style={{ maxWidth: 130, maxHeight: 55, objectFit: 'contain', alignSelf: 'flex-end', marginTop: 6 }} />
            )}
            <div style={{ fontSize: 9, mt: 1 }}>Authorised Signatory</div>
          </Box>
        </Box>
      </Box>
  );

  return (
    <Box>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 3mm !important; }
          html, body, #root { width: 100% !important; min-height: 0 !important; margin: 0 !important; padding: 0 !important; background: #fff !important; }
        }
      `}</style>
      {/* Centered Go Back and Print buttons */}
      {!hideActions && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => window.history.back()}>
            Go Back
          </Button>
          <Button variant="outlined" onClick={handlePrint}>Print</Button>
        </Box>
      )}
      <Box
        ref={printRef}
        sx={{
          width: '210mm',
          minHeight: '297mm',
          boxShadow: 8,
          m: '12px auto',
          background: '#fff',
          overflow: 'hidden',
          boxSizing: 'border-box',
          p: '0.5%',
          '@media print': {
            width: '100%',
            minHeight: '100%',
            margin: 0,
            padding: 0,
            boxShadow: 'none',
            borderRadius: 0,
            border: 'none',
            boxSizing: 'border-box',
            printColorAdjust: 'exact',
            WebkitPrintColorAdjust: 'exact',
          },
        }}
      >
        {displayCopies.map((copyLabel, idx) => (
          <Box
            key={idx}
            className="print-invoice-only"
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              mb: idx < displayCopies.length - 1 ? 4 : 0,
              '@media print': {
                border: 'none',
                mb: 0,
                height: '291mm',
                minHeight: '291mm',
                maxHeight: '291mm',
                overflow: 'hidden',
                pageBreakBefore: idx > 0 ? 'always' : 'auto',
                pageBreakInside: 'avoid',
              },
            }}
          >
            <Box sx={{ border: BORDER, overflow: 'hidden', position: 'relative', flexShrink: 0, '@media print': { border: 'none' } }}>
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    color: '#1f4d6b',
                    fontSize: 96,
                    fontWeight: 700,
                    lineHeight: 1,
                    opacity: 0.09,
                    textAlign: 'center',
                    transform: 'rotate(-32deg)',
                    maxWidth: '96%',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {watermarkName}
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                {renderInvoiceBody(copyLabel, `invoice-copy-${idx}`)}
              </Box>
            </Box>
            <Box sx={{ position: 'relative', zIndex: 3, mt: 'auto', ml: 0.8, mb: 0.55, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.2, color: '#64748b' }}>
              <Typography component="span" sx={{ fontSize: 8, lineHeight: 1, whiteSpace: 'nowrap' }}>Powered by :-</Typography>
              <Box component="img" src="/logo-transparent-expendable.png" alt="" sx={{ display: 'block', width: 288, maxHeight: 68, objectFit: 'contain', objectPosition: 'left center', opacity: 0.2 }} />
            </Box>
          </Box>
        ))}
      </Box>
      {/* Bottom center Print and Go Back buttons inline */}
      {!hideActions && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, mb: 2 }}>
          <Button variant="outlined" onClick={handlePrint}>Print</Button>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => window.history.back()}>
            Go Back
          </Button>
        </Box>
      )}
    </Box>
  );
}

/* =========================================================
   NUMBER TO WORDS (INR SIMPLE)
========================================================= */
function numberToWords(num) {
  if (!num) return 'Zero';

  const a = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100)
      return `${b[Math.floor(n / 10)]} ${a[n % 10]}`.trim();
    if (n < 1000)
      return `${a[Math.floor(n / 100)]} Hundred ${inWords(n % 100)}`.trim();
    if (n < 100000)
      return `${inWords(Math.floor(n / 1000))} Thousand ${inWords(
        n % 1000
      )}`.trim();
    if (n < 10000000)
      return `${inWords(Math.floor(n / 100000))} Lakh ${inWords(
        n % 100000
      )}`.trim();
    return `${inWords(Math.floor(n / 10000000))} Crore ${inWords(
      n % 10000000
    )}`.trim();
  }

  return inWords(Math.floor(num));
}
